import { LocalDate } from "@zap/utils/lib/DateTime";

/** Override these properties with localized versions */
export const LocalText = {
    Columns: "Columns",
    Filter: "Filter",
    ClearFilters: "Clear filters",
    AllDataFilteredOut: "No items match the current filters",
    All: "All",
    Minimum: "Minimum",
    Maximum: "Maximum",
    None: "None",
    AnyDate: "Any date",
    Today: "Today",
    From: "From",
    To: "To",
    MultiSelect: "Multi-select",
    HelpLink: "More info"
};

/** Override these properties with localized versions */
export const Formatting = {
    /** Locale name (e.g. 'en-US') */
    locale: navigator.language as Intl.UnicodeBCP47LocaleIdentifier,
    formatNumber: (number: Number) => number.toLocaleString(),
    parseNumber: (number: string) => parseNumber(number, Formatting.locale),
    /** Format date for use in an input, where the date will need to be parsed again later. */
    formatDate: (date: LocalDate) => date.inUserTime().toLocaleDateString('en-US'),
    parseDate: (date: string) => LocalDate.fromUserTime(new Date(date)),
}

export interface IDateFormatterOptions {
    /** Optional locale name (e.g. 'en-US'). If not specified, the current value of {@link Formatting.locale} will be used when formatting. */
    locale?: Intl.UnicodeBCP47LocaleIdentifier;
    /** Optional time zone. If not specified, the user's time zone will be used. */
    timeZone?: string;
}

/** 
 * Standard date/time formatting for UI.
 * Recommended to display a short version on screen, with the long version in a tooltip.
 **/
export class DateFormatter {
    constructor(private _options: IDateFormatterOptions = {}) { }

    /** e.g. Thursday, February 2, 2023 at 2:28:23 PM GMT+10 */
    dateTimeLong(dateTime: Date) {
        return dateTime.toLocaleString(this.locale, { dateStyle: 'full', timeStyle: 'long', timeZone: this._options.timeZone });
    }

    /** e.g. Thu, Feb 02, 21:10 */
    dateTimeShort(dateTime: Date) {
        return dateTime.toLocaleString(this.locale, { month: 'short', weekday: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: this._options.timeZone });
    }

    /** e.g. 2 days ago / in 2 days */
    dateTimeRelative(dateTime: Date) {
        // Ranges copied from https://momentjs.com/docs/#/displaying/fromnow/
        let ms = +dateTime - Date.now();
        let seconds = ms / 1000;
        let minutes = ms / 1000 / 60;
        let hours = ms / 1000 / 60 / 60;
        let days = ms / 1000 / 60 / 60 / 24;
        let months = ms / 1000 / 60 / 60 / 24 / 30;
        let years = ms / 1000 / 60 / 60 / 24 / 365;
        let relative = new Intl.RelativeTimeFormat(this.locale, { numeric: 'auto' });
        if (Math.abs(seconds) <= 44)
            return relative.format(0, 'minutes');
        if (Math.abs(minutes) <= 44)
            return relative.format(Math.round(minutes), 'minutes');
        if (Math.abs(hours) <= 21)
            return relative.format(Math.round(hours), 'hours');
        if (Math.abs(days) <= 25)
            return relative.format(Math.round(days), 'days');
        if (Math.abs(days) <= 319)
            return relative.format(Math.round(months), 'months');
        return relative.format(Math.round(years), 'years');
    }

    /** e.g. February 2, 2023 */
    dateLong(date: Date): string;
    dateLong(date: LocalDate): string;
    dateLong(date: Date | LocalDate) {
        return (date instanceof LocalDate
            ? date.inUserTime()
            : date
        ).toLocaleString(this.locale, { dateStyle: 'long', timeZone: this._options.timeZone });
    }

    /**
     * Returns a date similar to {@link dateTimeShort} e.g. Thu, Feb 02. 
     */
    dateShort(date: Date): string;
    dateShort(date: LocalDate): string;
    dateShort(date: Date | LocalDate) {
        let options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: '2-digit', timeZone: this._options.timeZone };
        return (date instanceof LocalDate
            ? date.inUserTime()
            : date
        ).toLocaleString(this.locale, options);
    }

    /**
     * Converts a dateTime similar to {@link dateTimeShort}
     * @param force12hr If set to true will force 12 hour time regardless of the locale's preference, otherwise will go with the locale's preference.
     */
    timeShort(dateTime: Date, force12hr: boolean = false) {
        let options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', timeZone: this._options.timeZone };

        if (force12hr)
            options.hour12 = true;

        return dateTime.toLocaleString(this.locale, options);
    }

    get locale() {
        return this._options.locale
            ?? Formatting.locale;
    }
}

export const dateFormatter = new DateFormatter();

const parsers: Record<string, NumberParser> = {};

function parseNumber(number: string, locale: string) {
    let parser = parsers[locale] ??= new NumberParser(locale);
    return parser.parse(number);
}

/** Based on https://observablehq.com/@mbostock/localized-number-parsing */
class NumberParser {
    private _group: RegExp;
    private _decimal: RegExp;

    constructor(locale: string) {
        let parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
        this._group = new RegExp(`[${parts.findOrThrow(d => d.type == 'group').value}]`, 'g');
        this._decimal = new RegExp(`[${parts.findOrThrow(d => d.type == 'decimal').value}]`);
    }

    parse(number: string) {
        return (number = number.trim()
            .replace(this._group, '')
            .replace(this._decimal, '.')) ? +number : NaN;
    }
}